import { useEffect } from 'react';
import { photoUrl } from '@/api/tournament';
import { cn } from '@/lib/utils';

type Props = {
  /** Which entry to view photos for. */
  entryId: number;
  /** Player display name for the modal header (optional). */
  title?: string;
  /** Starting balance the player entered (null if not yet uploaded). */
  startingBalance: number | null;
  /** Ending balance the player entered (null if not yet uploaded). */
  endingBalance: number | null;
  /** Whether each photo is available server-side. */
  hasStartingPhoto: boolean;
  hasEndingPhoto: boolean;
  onClose: () => void;
};

/**
 * Lightweight verification surface. Lazy-loads the two photos by setting
 * the <img> src only when the modal is open (this component only renders
 * when open). Each <img> is `loading="lazy"` for good measure.
 *
 * Auth gates are enforced by the /api/photos/:entryId/:which route —
 * the modal just renders <img> tags. If the caller isn't authorized
 * (admin or owner), the image requests will 403/404 and the alt text
 * shows. No extra client check needed.
 */
export function PhotoModal({
  entryId,
  title,
  startingBalance,
  endingBalance,
  hasStartingPhoto,
  hasEndingPhoto,
  onClose,
}: Props) {
  // Esc to close + scroll lock while open.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      window.removeEventListener('keydown', onKey);
      document.body.style.overflow = prev;
    };
  }, [onClose]);

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label="Entry photos"
      className="fixed inset-0 z-50 flex items-start sm:items-center justify-center bg-black/85 px-4 py-6 overflow-y-auto"
      onClick={onClose}
    >
      <div
        className="cabinet-card max-w-2xl w-full p-4 sm:p-5 space-y-4"
        onClick={(e) => e.stopPropagation()}
      >
        <header className="flex items-center justify-between gap-3">
          <h3 className="font-display text-cabinet-gold text-lg uppercase tracking-widest">
            Photos {title ? `· ${title}` : ''}
          </h3>
          <button
            type="button"
            onClick={onClose}
            className="text-cabinet-cream/60 hover:text-cabinet-cream w-10 h-10 rounded-full text-xl leading-none min-h-[44px] min-w-[44px]"
            aria-label="Close"
          >
            ×
          </button>
        </header>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <PhotoPanel
            entryId={entryId}
            which="start"
            label="Starting balance"
            balance={startingBalance}
            present={hasStartingPhoto}
          />
          <PhotoPanel
            entryId={entryId}
            which="end"
            label="Ending balance"
            balance={endingBalance}
            present={hasEndingPhoto}
          />
        </div>
      </div>
    </div>
  );
}

function PhotoPanel({
  entryId,
  which,
  label,
  balance,
  present,
}: {
  entryId: number;
  which: 'start' | 'end';
  label: string;
  balance: number | null;
  present: boolean;
}) {
  return (
    <div className="space-y-2">
      <p className="text-[10px] uppercase tracking-widest text-cabinet-cream/60">
        {label}
      </p>
      <div
        className={cn(
          'rounded-md ring-1 ring-cabinet-cream/15 bg-black/40 overflow-hidden',
          'aspect-[4/3] flex items-center justify-center',
        )}
      >
        {present ? (
          <img
            src={photoUrl(entryId, which)}
            alt={`${label} photo`}
            loading="lazy"
            decoding="async"
            className="w-full h-full object-contain"
          />
        ) : (
          <p className="text-cabinet-cream/40 text-sm">No photo</p>
        )}
      </div>
      <p className="text-xs text-cabinet-cream/60">
        Player-entered:{' '}
        <span className="font-led tabular-nums text-cabinet-led-on">
          {balance ?? '—'}
        </span>
      </p>
    </div>
  );
}
